import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { safeLocalStorage } from '~/store/safeLocalStorage'
import { EvmAddress } from '~/utils/address'

import type { Events } from 'porto/remote'

export type ConnectPayload = {
  chainIds?: readonly number[]
  mode?: string
  origin?: string
  domain: string
}

export type PendingRequest = NonNullable<
  Events.onDialogRequest.Payload['request']
>

interface ConnectState {
  connectPayload?: ConnectPayload
  pendingRequest?: PendingRequest
  activeConnections: {
    [key: string]: {
      orgId: string
      smartAccountAddress: EvmAddress.Type
    }
  }
  transactionState: {
    [key: number]: {
      status: 'pending' | 'created' | 'submitted'
      transactionId?: string
    }
  }
  messageState: {
    [key: number]: {
      status: 'pending' | 'created' | 'submitted'
      messageId?: string
    }
  }
  setConnectPayload: (connectPayload: ConnectPayload | undefined) => void
  setPendingRequest: (request: PendingRequest | undefined) => void
  setActiveConnection: (
    domain: string,
    orgId: string,
    smartAccountAddress: EvmAddress.Type,
  ) => void
  getActiveConnection: (
    domain: string,
  ) => { orgId: string; smartAccountAddress: EvmAddress.Type } | undefined
  getActiveConnections: () => ConnectState['activeConnections']
  getTransactionState: (
    requestId: number,
  ) => ConnectState['transactionState'][number]
  setTransactionState: (
    requestId: number,
    status: 'pending' | 'created' | 'submitted',
    transactionId?: string,
  ) => void
  getMessageState: (requestId: number) => ConnectState['messageState'][number]
  setMessageState: (
    requestId: number,
    status: 'pending' | 'created' | 'submitted',
    messageId?: string,
  ) => void
}

export const useConnectStore = create<ConnectState>()(
  persist(
    (set, get) => ({
      connectPayload: undefined,
      pendingRequest: undefined,
      activeConnections: {},
      transactionState: {},
      messageState: {},
      setConnectPayload: (connectPayload) => set({ connectPayload }),
      setPendingRequest: (pendingRequest) => set({ pendingRequest }),
      setActiveConnection: (
        domain: string,
        orgId: string,
        smartAccountAddress: EvmAddress.Type,
      ) =>
        set({
          activeConnections: {
            ...get().activeConnections,
            [domain]: { orgId, smartAccountAddress },
          },
        }),
      getActiveConnection: (domain: string) => get().activeConnections[domain],
      getActiveConnections: () => get().activeConnections,
      getTransactionState: (requestId: number) =>
        get().transactionState[requestId],
      setTransactionState: (
        requestId: number,
        status: 'pending' | 'created' | 'submitted',
        transactionId?: string,
      ) =>
        set({
          transactionState: {
            ...get().transactionState,
            [requestId]: { status, transactionId },
          },
        }),
      getMessageState: (requestId: number) => get().messageState[requestId],
      setMessageState: (
        requestId: number,
        status: 'pending' | 'created' | 'submitted',
        messageId?: string,
      ) =>
        set({
          messageState: {
            ...get().messageState,
            [requestId]: { status, messageId },
          },
        }),
    }),
    {
      name: 'connect-store', // key in localStorage
      storage: safeLocalStorage,
      version: 2,
      migrate: (persistedState) => ({
        activeConnections:
          (persistedState as Partial<ConnectState>).activeConnections ?? {},
      }),
      // persist only the fields you want (optional)
      partialize: (state) => ({
        activeConnections: state.activeConnections,
      }),
    },
  ),
)
